/**
 * **The resolver: one stimulus, one statement** (docs/RESOLUTION.md, settled
 * 2026-09-07; steps 1 to 6 of §1's pipeline, steps 1 and 2 of §12).
 *
 * Four places used to decide what a person's act meant, none of them knowing
 * the others (§0). This module is the layer above `decide` and the act
 * compilers: what does THIS stimulus mean, for THIS actor, in THIS context -
 * one pure function, one table of cases, deterministic, no I/O. The server
 * twin (`src/server/resolution.ts`) executes what it returns, in one
 * transaction; the dry run (`plan`) runs the same table over the read layer.
 *
 * **One file per pipeline step** (the read-cost pass, 2026-09-22): an agent
 * building one step used to open all 1655 lines of the old single module. The
 * directory now mirrors §1's numbering, and this barrel re-exports exactly what
 * that module exported, under the same names, so `@/domain/resolution` is
 * unchanged for every importer.
 *
 *   `types.ts`         - every shape the steps pass each other, plus
 *                        `PLAN_MAX_ITEMS`. Imports nothing from the directory.
 *   `stimulus.ts`      - step 1: what arrived, and the gesture key's hashes.
 *   `context.ts`       - step 2: `assembleContext` over the facts both
 *                        substrates gather (§9), and the log-side recovery.
 *   `clarification.ts` - step 3: the questions, the sweep, the per-mode answer.
 *   `intent.ts`        - step 4: the table, one row per stimulus kind.
 *   `permissions.ts`   - step 5: the lanes a missing right opens.
 *   `plan.ts`          - step 6: the fold through `decide`, and consequences.
 *   `resolve.ts`       - the entry that runs steps 2 to 6 in order.
 *   `policies.ts`      - §3: which questions a machine's act can hit, and the
 *                        policies a rule answers them with. Types-only leaf.
 *   `wire.ts`          - step 1 off the wire: what a valid queued stimulus is
 *                        (the offline replay's decoder, docs/SYNC.md §7.4).
 *   `legacy-statement.ts` - a stored statement read back as a descriptor: the
 *                        English sentences rows held before 2026-10-03.
 *
 * What it composes and never re-implements: `decide` (legality per command),
 * `meansApplyPlan` (attribute pairs to ordered commands: Reopen first, Close
 * last), `attributeLaneCommands` (a lane value's single pair), `mayAct` (the
 * Read rung), and the seat facts of `domain/seats.ts`.
 *
 * Three pieces sit beside a step rather than in a step of their own, and the
 * seam says why:
 *
 * - `shared.ts` holds the one genuinely cross-cutting helper (the adapter over
 *   an act compiler's apply plan); steps 4 and 6 both use it. The words every
 *   step speaks are catalog keys (src/i18n/messages/en/resolution.ts).
 * - §6's `transitionOf` (flows ride the plan) lives in `intent.ts`: it
 *   decorates the intent, and the tick rows are its only callers.
 * - §8's `commandOfEvent` recovery lives in `context.ts`: the steps it reads
 *   back out of the log are a piece of context (`Context.originSteps`),
 *   assembled before the pipeline runs rather than by any step of it.
 */

export {
  assembleContext,
  authoredReviewDuty,
  commandOfEvent,
  flowReviewers,
  flowsCoverDelivery,
  RETIRED_ACTIONS,
  stagesAuthorReviewDuty,
  UNTICK_DECIDING_KINDS,
  UNTICK_TAKES_BACK,
  untickOrigin,
} from './context';
export { CUSTOM_ANSWER } from './clarification';
export type { ContextFacts, FlowFacts, PlanEvent, ReviewDutySource, StageFacts } from './context';
export { edgeFactsOf, inversePlan } from './intent';
export { parseLegacyStatement, readOrigin, readStatement } from './legacy-statement';
export { contextMayAct } from './permissions';
export {
  describePolicy,
  FORCE_ANSWER,
  GO_AHEAD_ANSWER,
  isQuestionClass,
  LEAVE_ANSWER,
  parsePolicy,
  POLICY_ANSWERS,
  reachableQuestionClasses,
  SWEEP_ANSWER,
} from './policies';
export { foldPlanState } from './plan';
export { resolve } from './resolve';
export {
  mayConclude,
  needsItemTags,
  pendingOf,
  planId,
  stimulusOf,
  targetTowardOf,
} from './stimulus';
export { PLAN_MAX_ITEMS } from './types';
export type {
  AccessContext,
  ItemAccessFacts,
  ActTarget,
  Answer,
  ChildContext,
  Context,
  Intent,
  IntentKind,
  MembershipContext,
  Mode,
  Origin,
  OriginItem,
  OriginKind,
  ParentStub,
  PendingStimulus,
  Plan,
  PlanStep,
  Policy,
  Question,
  QuestionClass,
  QuestionOption,
  QuestionSubject,
  Resolution,
  Stimulus,
  StimulusEdge,
  StimulusHead,
  StoredOrigin,
  StimulusSource,
  TickMeans,
  Waiting,
  Weight,
} from './types';
export { decodeCommand, decodePendingStimulus, decodeQueuedGesture } from './wire';
export type { DecodedGesture, ExpectedAtoms } from './wire';
